import { Box, CircularProgress, Grid, useTheme } from '@mui/material';
import { useState, useEffect, useRef } from 'react';

import { ID_SITE } from '@shared/conf/env';
import { getUser } from '@shared/helpers/auth';

import TermsAndConditionsCheckbox from '@shared/components/TermsAndConditionsCheckbox';

import { StyledButtonConfirmar } from '@shared/components/Buttons/styles';

import { useFormik } from 'formik';

import {
  obtenerNombresRepetidos,
  noTravelContact,
  contactInfo,
  obtainQuantityPassenger,
  passengerForBodyPNR,
} from '@shared/helpers/flights/reserve';

import FormPersonalData from './FormData/FormPersonalData';

import NoTravelContact from './FormData/NoTravelContact';
import ContactInfo from './FormData/ContactInfo';
import PayContact from './FormData/PayContact';

import { reserveValidationSchema } from './validations';
import EnteredDataSection from '@shared/components/EnteredDataSection';

import { usePayeeSelection } from '@shared/components/PayeeSelector/usePayeeSelection';

const findFirstErrorField = (errors, parent = '') => {
  if (!errors || typeof errors !== 'object') return null;

  for (const key in errors) {
    const errorValue = errors[key];
    const fieldPath = parent ? `${parent}.${key}` : key;

    const isTerminalError =
      typeof errorValue === 'string' ||
      (typeof errorValue === 'object' && errorValue?.error);

    if (isTerminalError) {
      return fieldPath;
    }

    if (typeof errorValue === 'object') {
      const nestedError = findFirstErrorField(errorValue, fieldPath);
      if (nestedError) {
        return nestedError;
      }
    }
  }

  return null;
};

const FlightsReserve = ({
  submitReserve,
  infoFlightsSearch,
  flightValidate = {},
  setIsLoading,
  isBuy,
  isLoading,
}) => {
  const ID_SITE_CONST = Number(ID_SITE);
  const actualUser = getUser();

  const theme = useTheme();

  const submitClickedRef = useRef(false);

  const [allCuit, setAllCuit] = useState(false);

  const [arrEqualNames, setArrEqualNames] = useState([]);

  const payeeState = usePayeeSelection({ forcePointOfSale: true });

  const payee = {
    ...payeeState,
    isPointOfSale: true,
    mainSellers: [],
    seller: null,
    handleSelectSeller: () => {},
  };

  const initialValues = {
    passengers: obtainQuantityPassenger(infoFlightsSearch),
    noTraveler: noTravelContact,
    contactInfo: contactInfo,
    id_entity: payee.subsidiary?.id,
    acceptedTerms: false,
  };

  const lastFlight = flightValidate?.cluster?.legs
    ?.at(-1)
    ?.options?.at(-1)
    ?.flights?.at(-1);

  const formik = useFormik({
    initialValues,
    validationSchema: reserveValidationSchema(lastFlight?.departure),
    onSubmit: async values => {
      if (submitClickedRef.current) return;

      submitClickedRef.current = true;
      setIsLoading(true);

      try {
        const bodyPNR = {
          id_integration: 2,
          id_user: actualUser ? actualUser.user.id : null,
          id_entity: values.id_entity,
          passengers: passengerForBodyPNR(
            values.passengers,
            values.contactInfo
          ),
          composition_price:
            flightValidate?.result?.cluster[0]?.composition_price,
          id_transaction: flightValidate?.id_transaction,
          email: {
            email_voucher: values.contactInfo.email,
            not_travel: {
              email: values.noTraveler.email,
              phone: `${values.noTraveler.codePhone}${values.noTraveler.caracteristicPhone}${values.noTraveler.phone}`,
              first_name: values.noTraveler.name,
              last_name: values.noTraveler.lastname,
            },
          },
        };

        await submitReserve(bodyPNR, {
          selectedSubsidiary: payee.subsidiary,
          contactInfo: values.contactInfo,
          passengers: values.passengers,
        });
      } finally {
        submitClickedRef.current = false;
        setIsLoading(false);
      }
    },
  });

  const handleChangeAllCuit = (cuitType, cuitNumber, currentIndex) => {
    setAllCuit(prevAllCuit => {
      const newAllCuit = !prevAllCuit;

      const updatedPassengers = formik.values.passengers.map((p, i) => {
        if (i === currentIndex) return p;

        if (newAllCuit) {
          // Aplica cuitType y cuitNumber actualizados del pasajero que activó
          return { ...p, cuitType, cuitNumber };
        } else {
          // Limpia los campos de los demás
          return { ...p, cuitType: '', cuitNumber: '' };
        }
      });

      formik.setFieldValue('passengers', updatedPassengers);
      return newAllCuit;
    });
  };

  useEffect(() => {
    setArrEqualNames(obtenerNombresRepetidos(formik?.values?.passengers));
  }, [formik.values.passengers]);

  useEffect(() => {
    if (!submitClickedRef.current) return;

    if (!formik.errors || Object.keys(formik.errors).length === 0) return;

    const firstErrorField = findFirstErrorField(formik.errors);

    if (firstErrorField) {
      const elementId = firstErrorField.replaceAll(/\.(\d+)\./g, '[$1].');
      const input = document.getElementById(elementId);

      if (input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus();
      }
    }

    submitClickedRef.current = false;
  }, [formik.errors, formik.submitCount]);

  const passengerTypeLabels = {
    0: p => `Adulto ${p.adultNumber}`,
    1: p => `Menor ${p.childNumber}`,
    2: p => `Infante ${p.babyNumber}`,
  };

  const fieldsList = formik?.values?.passengers?.map(passenger => ({
    Tipo: passengerTypeLabels[passenger.passengerType]?.(passenger) ?? '',
    Nombre: passenger.name,
    Apellido: passenger.lastname,
    Documento: passenger.dni ? `${passenger.typeDNI} ${passenger.dni}` : '',
  }));

  let contenidoBoton;

  if (isLoading) {
    contenidoBoton = <CircularProgress sx={{ color: theme.text1 }} size={25} />;
  } else if (isBuy) {
    contenidoBoton = 'Continuar';
  } else {
    contenidoBoton = 'Reservar';
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {formik.values.passengers.map((passenger, index) => (
          <FormPersonalData
            key={`FormPersonalData-key${+index}`}
            index={index}
            passenger={passenger}
            passengerIndex={index}
            handleChangeAllCuit={handleChangeAllCuit}
            allCuit={allCuit}
            showSwitch={index === 0 && formik.values.passengers.length > 1}
            fullNameEqual={arrEqualNames}
            formik={formik}
          />
        ))}

        <NoTravelContact formik={formik} />
        <ContactInfo formik={formik} />
        {ID_SITE_CONST !== 2 && (
          <PayContact isBuy={isBuy} formik={formik} payee={payee} />
        )}
        <EnteredDataSection title="Datos Ingresados" fieldsList={fieldsList} />

        <Grid
          container
          alignItems="center"
          sx={{ mt: 1, mb: 2 }}
          id="acceptedTerms"
        >
          <TermsAndConditionsCheckbox
            formik={formik}
            fieldName="acceptedTerms"
            dialogText="TÉRMINOS Y CONDICIONES GENERALES de GIT Operador Mayorista, para reserva de pasajes aereos en nuestra plataforma"
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'end' }}>
          <StyledButtonConfirmar
            variant="contained"
            id="continueButton"
            type="submit"
            disabled={!formik?.values?.acceptedTerms || isLoading}
          >
            {contenidoBoton}
          </StyledButtonConfirmar>
        </Grid>
      </Box>
    </form>
  );
};

export default FlightsReserve;
